import React, { useState, useCallback, useRef, useEffect } from "react";
import ReactCrop, { centerCrop, makeAspectCrop } from "react-image-crop";
import axios from "axios";
import {
  Backdrop,
  Box,
  ButtonBase,
  Grid,
  Modal,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  useTheme,
} from "@mui/material";

import "react-image-crop/dist/ReactCrop.css";
import { BCPUpFromLine, BCPXmark } from "../icons/bcp/BCPIcons";
import { getData, postData } from "./UtilityFunctions";
import { getEnvironmentVariable } from "../../global/environmentVariables";
import { useTranslation } from "react-i18next";

export async function uploadBas64ToS3(base64, options) {
  try {
    const [dataType, data] = base64?.split(";");

    if (dataType) {
      const [, mimeType] = dataType.split(":");
      const [, dataString] = data.split(",");

      var additionalBody = {};
      if (options?.path) {
        additionalBody.path = options.path;
      }
      if (options?.public) {
        additionalBody.public = options.public;
      }

      var apiFile = await postData("/files", {
        body: {
          mimetype: mimeType,
          ...additionalBody,
        },
      });

      if (apiFile?.url) {
        const configBlop = {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        };

        const blob = b64toBlob(dataString, mimeType);
        const file = blobToFile(blob, apiFile.fileName);
        await axios.put(apiFile.url, file, configBlop);
        return apiFile;
      }
      throw new Error("Error getting upload info");
    } else {
      throw new Error("Error with file info");
    }
  } catch (err) {
    console.error(err);
    throw err;
  }
}

const fetchFile = async (fileId, size) => {
  if (!fileId) return;

  var returnObj;
  try {
    if (size) {
      returnObj = await getData(`/files/${fileId}`, {
        queryStringParameters: {
          size: size,
        },
      });
    } else {
      returnObj = await getData(`/files/${fileId}`);
    }
  } catch (err) {
    console.error(err);
  }
  return returnObj;
};

export default function ImageUploader(props) {
  const {
    name,
    setFieldValue,
    fileId,
    size,
    aspectW,
    aspectY,
    noAspect,
    round,
  } = props;

  const { t } = useTranslation("translation", {
    keyPrefix: "components.eventCreation"
  });

  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [existingPhoto, setExistingPhoto] = useState(null);
  const [loading, setLoading] = useState(true);

  const [upImg, setUpImg] = useState();
  const imgRef = useRef(null);
  const previewCanvasRef = useRef(null);
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState(null);

  const onSelectFile = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const reader = new FileReader();
      reader.addEventListener("load", () => setUpImg(reader.result));
      reader.readAsDataURL(e.target.files[0]);
    }
  };

  const onLoad = useCallback(
    (img) => {
      imgRef.current = img;

      const { width, height } = img;

      const calculatedAspect = noAspect
        ? undefined
        : aspectW && aspectY
          ? aspectW / aspectY
          : 16 / 9;

      if (calculatedAspect) {
        const crop = centerCrop(
          makeAspectCrop(
            {
              unit: "px",
              width: Math.min(width * 0.9, height * 0.9 * calculatedAspect)
            },
            calculatedAspect,
            width,
            height
          ),
          width,
          height
        );
        setCrop(crop);
      } else {
        setCrop({
          unit: "px",
          x: width * 0.05,
          y: height * 0.05,
          width: width * 0.9,
          height: height * 0.9
        });
      }
    },
    [noAspect, aspectW, aspectY]
  );

  const onImageLoad = (e) => {
    onLoad(e.currentTarget);
  };

  useEffect(() => {
    const check = async () => {
      setLoading(true);
      if (!fileId) {
        setExistingPhoto(null);
        setLoading(false);
        return;
      }

      if (existingPhoto) {
        if (existingPhoto.id === fileId) {
          setLoading(false);
          return;
        }
        setExistingPhoto(null);
      }

      try {
        var file = await fetchFile(fileId, size);

        if (file) {
          setExistingPhoto({
            id: fileId,
            ...file,
          });
        }
      } catch (err) {
        //
      }
      setLoading(false);
    };
    check();
    // eslint-disable-next-line
  }, [fileId]);

  useEffect(() => {
    if (!completedCrop || !previewCanvasRef.current || !imgRef.current) {
      return;
    }

    const image = imgRef.current;
    const canvas = previewCanvasRef.current;
    const crop = completedCrop;

    // Introduced image Promise
    // Ensuring image is loaded properly before allowing user to crop
    const loadImage = (url) => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.addEventListener("load", () => resolve(img));
        img.addEventListener("error", (e) => reject(e));
        img.src = url;
      });
    };

    async function generatePreview() {
      try {
        const completedImg = await loadImage(upImg);

        const displayedWidth = image.width;
        const displayedHeight = image.height;
        const scaleX = completedImg.naturalWidth / displayedWidth;
        const scaleY = completedImg.naturalHeight / displayedHeight;

        const ctx = canvas.getContext("2d");

        canvas.width = Math.floor(crop.width * scaleX);
        canvas.height = Math.floor(crop.height * scaleY);

        ctx.scale(1, 1);
        ctx.imageSmoothingQuality = "high";

        ctx.drawImage(
          completedImg,
          Math.floor(crop.x * scaleX),
          Math.floor(crop.y * scaleY),
          Math.floor(crop.width * scaleX),
          Math.floor(crop.height * scaleY),
          0,
          0,
          Math.floor(crop.width * scaleX),
          Math.floor(crop.height * scaleY)
        );
      } catch (e) {
        console.error("Error generating preview:", e);
      }
    }

    generatePreview();
  }, [completedCrop, upImg]);

  return (
    <>
      {round ? (
        <ImageUploaderRound
          {...props}
          loading={loading}
          setOpen={setOpen}
          theme={theme}
          existingPhoto={existingPhoto}
        />
      ) : (
        <ImageUploaderRectangle
          {...props}
          loading={loading}
          setOpen={setOpen}
          theme={theme}
          existingPhoto={existingPhoto}
        />
      )}

      <Modal
        disableAutoFocus
        open={open}
        onClose={() => {
          setOpen(false);
        }}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: ["95vw", "85vw", 600],
            maxHeight: "95vh",
            overflowY: "scroll",
            bgcolor: "background.paper",
            border: "1px solid rgba(0, 0, 0, 0.12)",
            borderRadius: 1,
            boxShadow: 24,
            p: 4,
          }}
        >
          <IconButton
            aria-label="cancel"
            color="error"
            sx={{ position: "absolute", top: "4px", right: "4px" }}
            onClick={() => {
              setOpen(false);
            }}
          >
            <BCPXmark />
          </IconButton>
          <Grid
            container
            spacing={2}
            sx={{
              pb: 1,
              mb: 2,
              borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
            }}
          >
            <Grid item xs={12}>
              <Typography variant="h4" component="h2">
                {t("imageDetails")}
              </Typography>
            </Grid>
          </Grid>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <input type="file" accept="image/*" onChange={onSelectFile} />
            </Grid>
            <Grid item xs={12}>
              {upImg && (
                <Box
                  sx={{
                    maxWidth: "400px",
                    margin: "0 auto",
                    "& .ReactCrop": {
                      maxWidth: "100%",
                      margin: "0 auto",
                    },
                  }}
                >
                  <ReactCrop
                    crop={crop}
                    onChange={(c) => setCrop(c)}
                    onComplete={(c) => setCompletedCrop(c)}
                    aspect={
                      noAspect
                        ? undefined
                        : aspectW && aspectY
                          ? aspectW / aspectY
                          : 16 / 9
                    }
                    minWidth={100}
                    minHeight={100}
                  >
                    <img
                      src={upImg}
                      onLoad={onImageLoad}
                      alt="Crop preview of uploaded profile pic"
                      style={{
                        maxWidth: "100%",
                        maxHeight: "400px",
                        objectFit: "contain",
                      }}
                    />
                  </ReactCrop>
                  {completedCrop && (
                    <Box sx={{ mt: 2 }}>
                      <canvas
                        ref={previewCanvasRef}
                        style={{
                          width: "100%",
                          height: "auto",
                          display: completedCrop ? "block" : "none",
                        }}
                      />
                    </Box>
                  )}
                </Box>
              )}
            </Grid>

            <Grid item xs={12}>
              <Button
                variant="contained"
                color="success"
                fullWidth
                disabled={!completedCrop?.width || !completedCrop?.height}
                onClick={(e) => {
                  if (!previewCanvasRef.current) {
                    return;
                  }

                  setFieldValue(
                    name,
                    previewCanvasRef.current.toDataURL("image/png")
                  );
                  setOpen(false);
                }}
              >
                {t("save")}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Modal>
    </>
  );
}

function ImageUploaderRectangle(props) {
  const {
    sx,
    disabled,
    setOpen,
    values,
    existingPhoto,
    defaultUrl,
    theme,
    label,
    loading,
    name,
    aspectW,
    aspectY,
    matchAspectRatio,
  } = props;

  return (
    <ButtonBase
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        maxHeight: "388px",

        aspectRatio: matchAspectRatio
          ? `${aspectW || 16} / ${aspectY || 9}`
          : "",

        p: 1,
        border: 1,
        borderColor: "divider",
        "&:hover": {
          backgroundColor: "rgb(46 55 77 / 24%)",
        },
        backgroundColor: "background.main",

        overflow: "hidden",
        ...sx,
      }}
      onClick={() => setOpen(true)}
      disabled={disabled}
    >
      <Box
        sx={{
          width: "100%",
          height: "100%",
          overflow: matchAspectRatio ? "hidden" : "visible",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <img
          src={
            values?.[name] ||
            existingPhoto?.url ||
            values?.photoUrl ||
            defaultUrl ||
            `${getEnvironmentVariable(
              "SYSTEM_IMAGES_CLOUDFRONT"
            )}/images/EventImage${
              theme.palette.mode === "dark" ? "Dark" : ""
            }.png`
          }
          alt={label}
          style={
            matchAspectRatio
              ? {
                  objectFit: "cover",
                  width: "100%",
                  height: "100%",
                }
              : { maxWidth: "100%", maxHeight: "372px" }
          }
        />
        {loading && (
          <Box
            sx={{
              position: "absolute",
              width: "100%",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              textAlign: "center",
            }}
          >
            <CircularProgress size={100} />
          </Box>
        )}
        {!disabled && (
          <Box
            sx={{
              position: "absolute",
              bottom: "0px",
              width: "100%",
              right: "4px",
              textAlign: "right",
            }}
          >
            <BCPUpFromLine size={20} sx={{ margin: 2 }} />
          </Box>
        )}
      </Box>
    </ButtonBase>
  );
}

function ImageUploaderRound(props) {
  const {
    sx,
    disabled,
    setOpen,
    values,
    existingPhoto,
    defaultUrl,
    theme,
    label,
    loading,
    name,
    aspectW,
    aspectY,
    matchAspectRatio,
  } = props;

  return (
    <ButtonBase
      sx={{
        width: "100%",
        height: "auto",
        maxWidth: "388px",

        aspectRatio: matchAspectRatio
          ? `${aspectW || 1} / ${aspectY || 1}`
          : "1 / 1",
        overflow: "hidden",
        p: 1,

        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "background.main",

        border: 1,
        borderRadius: "50%",
        borderColor: "divider",
        "&:hover": {
          backgroundColor: "rgb(46 55 77 / 24%);",
        },
        ...sx,
      }}
      onClick={() => setOpen(true)}
      disabled={disabled}
    >
      <Box
        sx={{
          width: "100%",
          height: "100%",
          borderRadius: "50%",
          overflow: "hidden",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <img
          src={
            values?.[name] ||
            existingPhoto?.url ||
            values?.photoUrl ||
            defaultUrl ||
            `${getEnvironmentVariable(
              "SYSTEM_IMAGES_CLOUDFRONT"
            )}/images/EventImage${
              theme.palette.mode === "dark" ? "Dark" : ""
            }.png`
          }
          alt={label}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
        />
        {loading && (
          <Box
            sx={{
              position: "absolute",
              width: "100%",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              textAlign: "center",
            }}
          >
            <CircularProgress size={100} />
          </Box>
        )}
        {!disabled && (
          <Box
            sx={{
              position: "absolute",
              bottom: "0px",
            }}
          >
            <BCPUpFromLine sx={{ margin: 2 }} size={20} />
          </Box>
        )}
      </Box>
    </ButtonBase>
  );
}

/**
 * Convert a base64 string in a Blob according to the data and contentType.
 *
 * @param b64Data {String} Pure base64 string without contentType
 * @param contentType {String} the content type of the file i.e (image/jpeg - image/png - text/plain)
 * @param sliceSize {Int} SliceSize to process the byteCharacters
 * @see http://stackoverflow.com/questions/16245767/creating-a-blob-from-a-base64-string-in-javascript
 * @return Blob
 */
function b64toBlob(b64Data, contentType, sliceSize) {
  contentType = contentType || "";
  sliceSize = sliceSize || 512;

  var byteCharacters = atob(b64Data);
  var byteArrays = [];

  for (var offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    var slice = byteCharacters.slice(offset, offset + sliceSize);

    var byteNumbers = new Array(slice.length);
    for (var i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }

    var byteArray = new Uint8Array(byteNumbers);

    byteArrays.push(byteArray);
  }

  var blob = new Blob(byteArrays, { type: contentType });
  return blob;
}

const blobToFile = (theBlob, fileName) => {
  theBlob.lastModifiedDate = new Date();
  theBlob.name = fileName;
  return theBlob;
};
